import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import BEM from '../../../../common/bem';
import { ReactComponent as DndStatusSvg } from '../../../../common/images/dnd-status.svg';
import { mobxInjectSelect } from '../../../../common/utils';
import { fetchLastAutoForwardData, ForwardedEntity } from '../../../../common/utils/autoForward';
import type { Entity, Conversation, Role, User } from '../../../../types';

const classes = BEM.with('DndBanner');

export const DndText = ({ dndText }: { dndText: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const overflow = ref.current.scrollHeight > ref.current.clientHeight;
      setIsOverflowing(overflow);
    }
    setIsExpanded(false);
  }, [dndText]);

  return (
    <div
      ref={ref}
      onClick={() => (isOverflowing || isExpanded) && setIsExpanded(!isExpanded)}
      className={classes('dnd-text', { isExpanded, isOverflowing })}
      title={dndText}
    >
      {dndText}
    </div>
  );
};

type DndBannerProps = {
  counterPartyProfile?: { displayName: string; id?: string; hasTeamAutoForward?: boolean };
  currentConversation: Conversation;
  currentOrganizationId: string;
  getAutoForwardEntityDisplayName: (entities: string[]) => string;
  hasCurrentConversation: boolean;
  isComposing: boolean;
  isDelegationBannerEnabled: boolean;
  isExtendedAutoForwardOptionsEnabled: boolean;
  isRecipientPickerOpen: boolean;
  logGainsightAnalytics?: (params: {
    tracker: { name: string; props?: Record<string, unknown> };
  }) => void;
  selectedRecipients: Entity[];
  showAutoForwardRecipientsPane: (params: {
    autoForwardRecipients: (ForwardedEntity | undefined)[];
    user: User;
    groupId?: string;
    groupType?: string;
    organizationId: string;
  }) => void;
};

const DndBannerComponent = ({
  counterPartyProfile,
  currentConversation,
  currentOrganizationId,
  getAutoForwardEntityDisplayName,
  hasCurrentConversation,
  isComposing,
  isDelegationBannerEnabled,
  isExtendedAutoForwardOptionsEnabled,
  isRecipientPickerOpen,
  logGainsightAnalytics,
  selectedRecipients,
  showAutoForwardRecipientsPane,
}: DndBannerProps) => {
  let showDndBanner = false;
  let dndText = 'Is Unavailable';
  let autoForwardDisplayName = '';
  let user: User | undefined;
  let isForwardingToTeam = false;

  const counterParty = currentConversation?.counterParty;
  const { p2pRecipient } = (counterParty || {}) as {
    p2pRecipient?: Entity;
  };
  const effectiveEntity = isDelegationBannerEnabled && p2pRecipient ? p2pRecipient : counterParty;

  if ((isComposing || isRecipientPickerOpen) && selectedRecipients?.length === 1) {
    if (selectedRecipients[0].dnd && selectedRecipients[0].$entityType === 'user') {
      showDndBanner = true;
      dndText = selectedRecipients[0].dndText || 'Is Unavailable';
      user = selectedRecipients[0] as unknown as User;

      if (selectedRecipients[0].profileByOrganizationId && currentOrganizationId) {
        const profile = selectedRecipients[0].profileByOrganizationId?.[currentOrganizationId];
        if (isExtendedAutoForwardOptionsEnabled) {
          const dndAutoForwardEntities = (profile?.dndAutoForwardEntities || []) as unknown as {
            entityType?: string;
          }[];
          autoForwardDisplayName =
            getAutoForwardEntityDisplayName(profile?.dndAutoForwardEntities || []) || '';

          if (
            Array.isArray(dndAutoForwardEntities) &&
            dndAutoForwardEntities.length === 1 &&
            dndAutoForwardEntities[0]?.entityType === 'team'
          ) {
            isForwardingToTeam = true;
          }
        } else {
          autoForwardDisplayName = profile?.autoForwardReceivers?.[0]?.displayName || '';
        }
      }
    }
  } else if (hasCurrentConversation) {
    let targetUser: User | null = null;

    if (effectiveEntity?.$entityType === 'user') {
      targetUser = effectiveEntity as unknown as User;
    } else if (isDelegationBannerEnabled && effectiveEntity?.$entityType === 'role') {
      const roleEntity = effectiveEntity as unknown as Role;
      const roleOwnerUser =
        roleEntity.members && roleEntity.members.length === 1
          ? roleEntity.members[0]
          : roleEntity.botUser;
      if (roleOwnerUser) {
        targetUser = roleOwnerUser as unknown as User;
      }
    }

    if (targetUser?.dnd) {
      showDndBanner = true;
      dndText = targetUser.dndText || 'Is Unavailable';
      autoForwardDisplayName = counterPartyProfile?.displayName || '';
      user = targetUser;
      if (isExtendedAutoForwardOptionsEnabled && counterPartyProfile?.hasTeamAutoForward) {
        isForwardingToTeam = true;
      }
    }
  }

  if (!showDndBanner) return null;

  const isMultipleRecipients = autoForwardDisplayName === 'Multiple recipients';

  const handleMultipleRecipientsClick = async () => {
    if (
      (!isMultipleRecipients && !isForwardingToTeam) ||
      !user ||
      !currentOrganizationId ||
      !showAutoForwardRecipientsPane
    ) {
      return;
    }

    logGainsightAnalytics?.({
      tracker: {
        name: 'Inbox | Messenger - View All Auto-Forward Recipients',
      },
    });

    try {
      const resolvedEntities = await fetchLastAutoForwardData(user, currentOrganizationId);
      if (resolvedEntities && resolvedEntities.length > 0) {
        showAutoForwardRecipientsPane({
          autoForwardRecipients: resolvedEntities,
          user,
          groupId: undefined,
          groupType: undefined,
          organizationId: currentOrganizationId,
        });
      }
    } catch (error) {
      console.error('Failed to fetch auto-forward entities:', error);
    }
  };

  return (
    <div className={classes()}>
      <div className={classes('dnd-status')}>
        <div className={classes('dnd-status-information')}>
          <div className={classes('dnd-status-svg')}>
            <DndStatusSvg />
          </div>
          <DndText dndText={dndText} />
        </div>
        {autoForwardDisplayName && (
          <div className={classes('dnd-auto-forward')}>
            Auto-forwarding to:
            {(isMultipleRecipients || isForwardingToTeam) && isDelegationBannerEnabled ? (
              <span
                className={classes('dnd-auto-forward-name', { clickable: true })}
                onClick={handleMultipleRecipientsClick}
                role="button"
                data-test-id="dnd-banner-multiple-recipients"
              >
                {autoForwardDisplayName}
              </span>
            ) : (
              <span className={classes('dnd-auto-forward-name')}>{autoForwardDisplayName}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const DndBanner = mobxInjectSelect<{}, DndBannerProps>({
  composeMessageStore: ['isComposing', 'isRecipientPickerOpen', 'selectedRecipients'],
  conversationStore: ['counterPartyProfile', 'currentConversation', 'hasCurrentConversation'],
  messengerStore: [
    'currentOrganizationId',
    'isDelegationBannerEnabled',
    'isExtendedAutoForwardOptionsEnabled',
  ],
  trackerStore: ['logGainsightAnalytics'],
  userStore: ['getAutoForwardEntityDisplayName', 'showAutoForwardRecipientsPane'],
})(observer(DndBannerComponent));
